import { useState } from 'react';

import { Flex } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import {
  clearFilterKey,
  clearUploadedCSVFile,
  setActiveTab,
  setCheckedEmployees,
  useCheckedBaseCohort,
  useCheckedEmployees,
  useCSVUploadEmployeeList,
  useCSVUploadFileName,
  useViewActions,
  useViewsQuery,
  VIEWS_ACTIONS,
} from 'reducers/viewsSlice';
import { checkCustomRenameLocationToSubsidiaryEnabled } from 'utils/helpers/featureFlags';

import { VIEWS_FILTER_KEYS, VIEWS_FILTER_KEYS_DISPLAY_NAMES } from 'constants/ProjectConstants';

import { TagOutlinedList } from 'uiToolkitV1/TagOutlinedList';
import { TextLabel } from 'uiToolkitV1/Typography';

import { useBaseCohorts } from '../Hooks/dataHooks';
import { DeleteCSVFileModal } from './DeleteCSVFileModal';

const getBaseCohorts = (cohortList) =>
  [...cohortList]
    // .filter((cohort) => [-1, -2, -3, -4, -5, -6, -7, -8, -9, -10].includes(cohort.id))

    .sort((a, b) => b.id - a.id)
    .map((cohort) => ({ ...cohort, uuid: String(cohort.id) }));

export const FiltersTagList = ({ showLabel = true }) => {
  const filters = useViewsQuery();
  const checkedEmployees = useCheckedEmployees();
  const { uuid: baseCohortUuid } = useCheckedBaseCohort();
  // const {
  //   data: { defaultList } = {},
  //   isLoading,
  //   isSuccess
  // } = useSidebarCohortViews(useSidebarNavMatchRoute());

  const dispatch = useDispatch();
  const viewAction = useViewActions();
  const isEditCustomAccess = viewAction === VIEWS_ACTIONS.EDIT_CUSTOM_ACCESS;

  // const baseCohorts = getBaseCohorts(defaultList ?? []);
  const { data: baseCohorts, isLoading, isSuccess } = useBaseCohorts();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const filterKeys = Object.keys(filters);
  const uploadedFileEmployeeList = useCSVUploadEmployeeList();
  const showFileUploadedTag = uploadedFileEmployeeList?.length > 0;

  let filtersTagList = filterKeys.map((filterKey) => {
    const isDisplayFilterValueInTag = !VIEWS_FILTER_KEYS_DISPLAY_NAMES[filterKey];
    let title = isDisplayFilterValueInTag ? filters[filterKey] : `${VIEWS_FILTER_KEYS_DISPLAY_NAMES[filterKey]}`;

    if (!isDisplayFilterValueInTag) {
      const truthyFilterValues = Object.keys(filters[filterKey]).filter((subKey) => !!filters[filterKey][subKey]);

      const count = Array.isArray(filters[filterKey]) ? filters[filterKey].length : truthyFilterValues.length;
      title = title === 'Goals' ? `${moduleName} (${count})` : `${title} (${count})`;
    }

    if (filterKey === 'location') {
      const truthyFilterValues = Object.keys(filters[filterKey]).filter((subKey) => !!filters[filterKey][subKey]);

      const count = Array.isArray(filters[filterKey]) ? filters[filterKey].length : truthyFilterValues.length;
      title = checkCustomRenameLocationToSubsidiaryEnabled() ? `Subsidiary (${count})` : `Location (${count})`;
    }

    return {
      id: filterKey,
      title,
    };
  });

  const employeesTag = {
    id: VIEWS_FILTER_KEYS.EMPLOYEES,
    title: `${VIEWS_FILTER_KEYS_DISPLAY_NAMES[VIEWS_FILTER_KEYS.EMPLOYEES]} (${checkedEmployees?.length})`,
  };

  if (!isLoading && isSuccess) {
    const baseCohort = baseCohorts.find((baseCohort) => baseCohort.uuid === baseCohortUuid);
    filtersTagList = [
      // { id: VIEWS_FILTER_KEYS.BASE_COHORT, title: baseCohort.name },
      ...(baseCohort ? [{ id: VIEWS_FILTER_KEYS.BASE_COHORT, title: baseCohort.name }] : [{}]),
      ...(checkedEmployees?.length > 0 ? [employeesTag] : []),
      ...filtersTagList,
    ];
  }

  const handleClearFilters = (id) => {
    if (id === VIEWS_FILTER_KEYS.EMPLOYEES) {
      dispatch(setCheckedEmployees([]));
    } else {
      dispatch(clearFilterKey(id));
    }
  };

  const onClickHandler = (id) => {
    // In editCustomAccess , we don't allow user to go to base cohort tab by clicking on the filter tag
    if (id === 'base_cohort' && isEditCustomAccess) {
      return;
    }
    dispatch(setActiveTab(id));
  };

  return (
    <>
      <Flex p={3} pb={2}>
        {showLabel && (
          <TextLabel bold color="text.g2" mt={1} mr={1} flexShrink={0}>
            User Filters:
          </TextLabel>
        )}
        <TagOutlinedList tagList={filtersTagList} onRemove={handleClearFilters} onClickHandler={onClickHandler} />
      </Flex>
      {showFileUploadedTag && <FileUploadTagList showLabel={showLabel} />}
    </>
  );
};

const FileUploadTagList = ({ showLabel }) => {
  const uploadedFileName = useCSVUploadFileName();
  const dispatch = useDispatch();
  const fileUploadedTags = [{ id: uploadedFileName, title: uploadedFileName }];
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onDeleteHandler = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Flex p={3} pb={2}>
        {showLabel && (
          <TextLabel bold color="text.g2" mt={1} mr={1} flexShrink={0}>
            Uploaded File:
          </TextLabel>
        )}
        <TagOutlinedList
          tagList={fileUploadedTags}
          onRemove={() => {
            dispatch(clearUploadedCSVFile());
            setIsModalOpen(true);
          }}
        />
      </Flex>
      <DeleteCSVFileModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} onDelete={onDeleteHandler} />
    </>
  );
};
